<script setup lang="ts">
/* eslint-env vue/setup-compiler-macros */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { computed } from 'vue';
import Chart from 'primevue/chart';
import DateRangePicker from '@/components/ui/DateRangePicker.vue';
import ToolbarIconButton from '@/components/ui/ToolbarIconButton.vue';
import IconExport from '@/components/icons/IconExport.vue';
import QueuedExportModal from '@/components/modals/QueuedExportModal.vue';
import { useReportsPortfolioRisk } from '@/composables/useReportsPortfolioRisk';
import { externalTooltip } from '@/utils/chartTooltip';
import { formatDate } from '@/utils/formatUtils';
import { exportPortfolioRiskReport } from '@/services/reportsService';
import { useQueuedExport } from '@/composables/useQueuedExport';
import { useDateRangeWithTimezone } from '@/composables/useDateRangeWithTimezone';

const CANVAS_W = 860;
const CANVAS_H = 580;

const { selectedDateRange } = useDateRangeWithTimezone();

const formattedDateRange = computed<[string, string]>(() => [
    formatDate(selectedDateRange.value?.[0] ?? new Date()),
    formatDate(selectedDateRange.value?.[1] ?? new Date()),
]);

const {
    chartData,
    percentages,
    loading,
    total,
    openCategory,
} = useReportsPortfolioRisk(formattedDateRange);

const onSliceSelect = ({ element }: { element: { index: number } }) => {
    openCategory(element.index);
};

const chartKey = computed(() => percentages.value.map((p) => p.percent).join('-'));

const {
    exporting,
    cooldownActive,
    queuedModalVisible,
    queuedTitle,
    queuedMessage,
    run: runExport,
} = useQueuedExport({
    queuedTitle: 'Portfolio risk export started',
    successDetail: 'Portfolio risk CSV downloaded.',
    errorDetail: 'Unable to export the portfolio risk report. Please try again.',
});

function exportCsv() {
    const [from, to] = formattedDateRange.value;

    runExport(() => exportPortfolioRiskReport({ from, to }));
}

const outerLabelsPlugin = [{
    id: 'outerLabels',
    afterDraw(chart: any) {
        const { ctx } = chart as { ctx: CanvasRenderingContext2D };
        const meta = chart.getDatasetMeta(0);
        if (!meta?.data?.length) return;

        const vals = (chart.data.datasets?.[0]?.data ?? []) as number[];
        const lbls = (chart.data.labels ?? []) as string[];
        if (!vals.length) return;

        const hasData = vals.some((v) => v > 0);
        if (!hasData) return;

        const GAP = 52; // px between arc edge and label anchor
        const PCT_FONT = '600 32px Archivo, sans-serif';
        const DESC_FONT = '400 14px Archivo, sans-serif';
        const MAX_DESC_W = 150;
        const LINE_H = 19;

        const wrap = (text: string, maxW: number): string[] => {
            ctx.font = DESC_FONT;
            const words = text.split(' ');
            const lines: string[] = [];
            let cur = '';
            for (const w of words) {
                const attempt = cur ? `${cur} ${w}` : w;
                if (ctx.measureText(attempt).width > maxW && cur) {
                    lines.push(cur);
                    cur = w;
                } else {
                    cur = attempt;
                }
            }
            if (cur) lines.push(cur);
            return lines;
        };

        meta.data.forEach((arc: any, i: number) => {
            if (!vals[i]) return;

            const {
                startAngle, endAngle, x: cx, y: cy, outerRadius,
            } = arc.getProps(['startAngle', 'endAngle', 'x', 'y', 'outerRadius'], true) as {
                startAngle: number; endAngle: number;
                x: number; y: number; outerRadius: number;
            };

            // Skip label if arc is too small to label without overlap (< 5° arc)
            if (endAngle - startAngle < 0.087) return;

            const mid = (startAngle + endAngle) / 2;
            const cosA = Math.cos(mid);
            const sinA = Math.sin(mid);

            // Anchor point just outside the arc
            const ax = cx + cosA * (outerRadius + GAP);
            const ay = cy + sinA * (outerRadius + GAP);

            const pct = `${percentages.value[i]?.percent ?? '0'}%`;
            const descLines = wrap(lbls[i] ?? '', MAX_DESC_W);

            // Text-align based on horizontal position of anchor
            let align: CanvasTextAlign;
            if (cosA < -0.2) align = 'right';
            else if (cosA > 0.2) align = 'left';
            else align = 'center';

            ctx.save();
            ctx.textAlign = align;

            // Percentage — large bold
            ctx.font = PCT_FONT;
            ctx.fillStyle = '#111111';
            ctx.textBaseline = 'bottom'; // pct sits just above anchor
            ctx.fillText(pct, ax, ay);

            // Description — smaller, below anchor
            ctx.font = DESC_FONT;
            ctx.fillStyle = '#111111';
            ctx.textBaseline = 'top';
            descLines.forEach((line, li) => {
                ctx.fillText(line, ax, ay + 4 + li * LINE_H);
            });

            ctx.restore();
        });
    },
}];

const chartOptions = {
    responsive: false,
    cutout: '45%',
    layout: {
        padding: {
            top: 90,
            bottom: 100,
            left: 200,
            right: 200,
        },
    },
    onHover: (event: any, elements: unknown[]) => {
        const canvas = event?.native?.target as HTMLElement | undefined;
        if (canvas) canvas.style.cursor = elements.length ? 'pointer' : 'default';
    },
    plugins: {
        legend: { display: false },
        tooltip: {
            ...externalTooltip({
                title: (items: Array<{ label: string }>) => items[0]?.label ?? '',
                label: (ctx: { dataIndex: number; parsed: number }) => {
                    const count = ctx.parsed ?? 0;
                    const percent = percentages.value[ctx.dataIndex]?.percent ?? '0';

                    return `${count.toLocaleString('en-US')} ${count === 1 ? 'transaction' : 'transactions'} (${percent}%)`;
                },
            }),

            position: 'average' as const,
        },
    },
};
</script>

<template>
    <div class="portfolio-risk-tab">
        <div class="portfolio-risk-tab__toolbar">
            <DateRangePicker
                v-model="selectedDateRange"
                label="Filter by Date:"
                trigger-width="350px"
                trigger-radius="10px"
            />

            <div class="portfolio-risk-tab__toolbar-right">
                <ToolbarIconButton
                    ariaLabel="Export"
                    :disabled="exporting || cooldownActive"
                    @click="exportCsv"
                >
                    <IconExport />
                </ToolbarIconButton>
            </div>
        </div>

        <div class="portfolio-risk-tab__chart-area">
            <div v-if="loading" class="portfolio-risk-tab__loading">
                <div class="portfolio-risk-tab__loading-ring"></div>
            </div>
            <div v-else-if="total === 0" class="portfolio-risk-tab__empty">
                <p class="portfolio-risk-tab__empty-title">No data available</p>
                <p class="portfolio-risk-tab__empty-text">There is no portfolio risk data for the selected date range.</p>
            </div>
            <Chart
                v-else
                :key="chartKey"
                type="doughnut"
                :data="chartData"
                :options="chartOptions"
                :plugins="outerLabelsPlugin"
                :width="CANVAS_W"
                :height="CANVAS_H"
                @select="onSliceSelect"
            />
        </div>
    </div>

    <QueuedExportModal
        v-model:visible="queuedModalVisible"
        :title="queuedTitle"
        :message="queuedMessage"
    />
</template>

<style lang="scss" scoped>
.portfolio-risk-tab {
    @include tab-base(25px 30px 30px);
}

.portfolio-risk-tab__toolbar {
    @include tab-toolbar(20px);
    margin-bottom: 25px;
}

.portfolio-risk-tab__toolbar-right {
    @include tab-toolbar-right;
}

.portfolio-risk-tab__chart-area {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
}

.portfolio-risk-tab__loading {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 300px;
}

.portfolio-risk-tab__loading-ring {
    width: 48px;
    height: 48px;
    border: 4px solid $border-gray-color;
    border-top-color: $main-orange;
    border-radius: 50%;
    animation: portfolio-risk-spin 0.75s linear infinite;
}

@keyframes portfolio-risk-spin {
    to { transform: rotate(360deg); }
}

.portfolio-risk-tab__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 30px;
    text-align: center;
    width: 100%;
}

.portfolio-risk-tab__empty-title {
    font-family: $font-family-archivo;
    font-weight: 400;
    font-size: 18px;
    line-height: 20px;
    color: $black-color;
    margin: 0 0 8px 0;
}

.portfolio-risk-tab__empty-text {
    font-family: $font-family-archivo;
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    color: rgba($black-color, 0.6);
    margin: 0;
}
</style>
